import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  ExportRiskScoresCommand,
  type ExportRiskScoresCommandInput,
  type ExportRiskScoresCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';

import { apiHeliosClient } from 'src/v2/shared/data';
import { RiskScoreQueryKeys } from './keys';

/**
 * Generates a CSV export of the business unit's top-level risk scores for one time window
 * (optionally narrowed to stations) and returns a short-lived presigned S3 download URL plus
 * generation metadata (`downloadUrl`, `recordCount`, `generatedAt`, `expiresAt`).
 *
 * User-triggered and non-cached, so this is a mutation rather than a cached query — the presigned
 * URL expires quickly, so there is nothing worth keeping in the query cache. No `withRetry`: the
 * export is a server-side generate, not an idempotent read, so a failure surfaces for the user to
 * retry explicitly rather than being retried under the hood.
 */
export function useExportRiskScoresMutation(
  options?: Omit<
    UseMutationOptions<
      ExportRiskScoresCommandOutput,
      Error,
      ExportRiskScoresCommandInput
    >,
    'mutationKey' | 'mutationFn'
  >,
): UseMutationResult<ExportRiskScoresCommandOutput, Error, ExportRiskScoresCommandInput> {
  return useMutation({
    ...options,
    mutationKey: [RiskScoreQueryKeys.exports],
    mutationFn: (input: ExportRiskScoresCommandInput) =>
      apiHeliosClient.send(new ExportRiskScoresCommand(input)),
  });
}
